/* eslint-disable @typescript-eslint/no-misused-promises */
import type { JSX } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@heroui/react';
import { Divider } from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useAuth } from '~/hooks/useAuth';

import { Password } from '~/components/Field';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '~/components/Modal';

const reauthPasswordSchema = z.object({
  password: z.string().min(1, 'Password is required'),
});
type ReauthPasswordData = z.infer<typeof reauthPasswordSchema>;

interface ReauthModalProps {
  isOpen: boolean;
  onConfirmed: () => Promise<void>;
  onCancel: () => void;
}

function parseReauthError(err: unknown): string {
  const code = (err as { code?: string }).code ?? '';
  if (code === 'auth/wrong-password' || code === 'auth/invalid-credential')
    return 'Incorrect password.';
  if (code === 'auth/too-many-requests')
    return 'Too many attempts — please wait before trying again.';
  if (code === 'auth/popup-closed-by-user')
    return 'The sign-in popup was closed. Please try again.';
  return 'Re-authentication failed. Please try again.';
}

export function ReauthModal({ isOpen, onCancel, onConfirmed }: ReauthModalProps): JSX.Element {
  const { reauthenticateWithGithub, reauthenticateWithGoogle, reauthenticateWithPassword, user } =
    useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const linkedProviders = user?.providerData.map(p => p.providerId) ?? [];
  const hasPassword = linkedProviders.includes('password');
  const hasGoogle = linkedProviders.includes('google.com');
  const hasGithub = linkedProviders.includes('github.com');

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<ReauthPasswordData>({
    resolver: zodResolver(reauthPasswordSchema),
    mode: 'onSubmit',
  });

  const handleClose = (): void => {
    reset();
    setError(null);
    onCancel();
  };

  const handlePasswordReauth = async (data: ReauthPasswordData): Promise<void> => {
    setError(null);
    setIsPending(true);
    try {
      await reauthenticateWithPassword(data.password);
      reset();
      await onConfirmed();
    } catch (err) {
      setError(parseReauthError(err));
    } finally {
      setIsPending(false);
    }
  };

  const handleOAuthReauth = async (provider: 'google' | 'github'): Promise<void> => {
    setError(null);
    setIsPending(true);
    try {
      if (provider === 'google') {
        await reauthenticateWithGoogle();
      } else {
        await reauthenticateWithGithub();
      }
      await onConfirmed();
    } catch (err) {
      setError(parseReauthError(err));
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={handleClose} maxWidth="sm" preventClosing={isPending}>
      <ModalContent>
        <ModalHeader>Confirm your identity</ModalHeader>
        <Divider />
        <ModalBody>
          <p className="text-sm text-default-500">
            For your security, please re-authenticate before continuing.
          </p>

          {hasPassword && (
            <form
              noValidate
              onSubmit={handleSubmit(handlePasswordReauth)}
              className="flex flex-col gap-4"
            >
              <Password
                id="reauth-password"
                label="Password"
                autoComplete="current-password"
                isRequired
                isInvalid={!!errors.password}
                errorMessage={errors.password?.message}
                {...register('password')}
              />
              <Button type="submit" color="primary" isDisabled={isPending} fullWidth>
                {isPending ? <LoadingSpinner /> : 'Confirm with Password'}
              </Button>
            </form>
          )}

          {(hasGoogle || hasGithub) && hasPassword && (
            <div className="flex items-center gap-2 my-1">
              <div className="flex-1 border-t border-default" />
              <span className="text-xs text-default-400">or</span>
              <div className="flex-1 border-t border-default" />
            </div>
          )}

          {hasGoogle && (
            <Button
              variant="bordered"
              onPress={() => void handleOAuthReauth('google')}
              isDisabled={isPending}
              fullWidth
            >
              Confirm with Google
            </Button>
          )}

          {hasGithub && (
            <Button
              variant="bordered"
              onPress={() => void handleOAuthReauth('github')}
              isDisabled={isPending}
              fullWidth
            >
              Confirm with GitHub
            </Button>
          )}

          {error && (
            <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-400 dark:border-danger-800 text-danger-700 dark:text-danger-400 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={handleClose} isDisabled={isPending}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
